import { useStore } from '../../../hooks/useStore';
import Collapse from '../../ui/Collapse';
import Alert from '../../ui/Alert';
import { UIColor } from '../../ui/types';
import { useEffect, useRef, useState } from 'react';
import { ToastItem } from '../../../store/stores/toast.store';
import IconError from '../../icons/IconError';
import IconCheckCircle from '../../icons/IconCheckCircle';

const ToastAlert = ({
  item,
  onRemove
}: {
  item: ToastItem;
  onRemove: (id: string) => void;
}) => {
  let icon = item.options?.icon;
  if (!icon) {
    switch (item.type) {
      case 'error':
        icon = <IconError />;
        break;
      case 'success':
        icon = <IconCheckCircle />;
        break;
    }
  }

  const timer = useRef<NodeJS.Timeout>();
  useEffect(() => {
    if (!item.options?.displayDuration) return;

    timer.current = setTimeout(() => {
      onRemove(item.id);
    }, item.options.displayDuration);

    // Cleanup
    return () => {
      if (timer.current) {
        clearTimeout(timer.current);
      }
    };
  }, [item]);
  return (
    <div
      className="pb-2"
      data-testid={
        item.options?.dataTestId ??
        `toast-${item.type ?? 'primary'}-${item.id}`
      }
    >
      <Alert
        className="pointer-events-auto shadow-md"
        color={item.type as UIColor}
        onClose={
          item.options?.dismissible ? () => onRemove(item.id) : undefined
        }
        icon={icon}
      >
        {item.message}
      </Alert>
    </div>
  );
};

const Toaster = () => {
  const { toast } = useStore();
  const { toastItems } = toast;
  const animDuration = 200;

  const [hideQueue, setHideQueue] = useState<Array<string>>([]);

  const handleRemove = (id: string) => {
    if (hideQueue.indexOf(id) !== -1) return;
    const q = [...hideQueue];
    q.push(id);
    setHideQueue(q);
    setTimeout(() => {
      toast.remove(id);
      const qR = [...hideQueue];
      qR.splice(qR.indexOf(id), 1);
      setHideQueue(qR);
    }, animDuration);
  };

  return (
    <div
      className="pointer-events-none fixed inset-x-0 top-2 z-modal flex max-h-full flex-col items-center"
      data-testid="toast-stack"
    >
      {toastItems?.map((item) => (
        <Collapse
          show={!hideQueue.includes(item.id)}
          key={item.id}
          duration={animDuration}
          className="inline-block"
          grow
        >
          <ToastAlert item={item} onRemove={handleRemove} />
        </Collapse>
      ))}
    </div>
  );
};

export default Toaster;
